import { useUpdateUser } from "@hooks/trpc/useMe";
import {
  generateCustomActionLinkAction,
  generateDirectLinkAction,
  generateLoginLinkAction,
  LinkAction,
} from "@hooks/useBranch/helpers";
import { useAuth } from "@utils/firebaseAuth/auth";
import { useBranchStore } from "@utils/branch/store";
import { track } from "@utils/rudderstack";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useEffect } from "react";
import branch from "branch-sdk";
import type { BranchParams } from "react-native-branch";
import {
  BranchLinkType,
  CustomActionLink,
  DirectLink,
  LoginLink,
} from "types/branch";
import { captureException } from "@utils/captureException";
import { capabilities } from "../../capabilities";

const useBranchIdentity = () => {
  const { firebaseUser: user, status } = useAuth();
  useEffect(() => {
    if (!capabilities.allowed("branch")) return;

    if (status === "logged-in" && user) {
      branch.setIdentity(user.uid, (error, data) => {
        if (error) {
          console.error(
            "::useBranch useBranchIdentity:: setIdentity error",
            error,
          );
        } else {
          console.log(
            "::useBranch useBranchIdentity:: setIdentity success",
            data,
          );
        }
      });
    }
    if (status === "no-user") {
      branch.logout((error) => {
        if (error) {
          console.error("::useBranch useBranchIdentity:: logout error", error);
        } else {
          console.log("::useBranch useBranchIdentity:: logout success");
        }
      });
    }
  }, [status]);
};

const useBranchSubscribe = (
  handleLink: (data: BranchParams) => Promise<void>,
) => {
  useEffect(() => {
    if (!capabilities.allowed("branch")) return;

    branch.data(async (error, data) => {
      if (error) {
        console.error("::useBranch useBranch:: data error", error);
        return;
      }
      console.log("::useBranch useBranch:: data", data);
      if (data) {
        const parsedData = data.data_parsed as BranchParams;
        if (
          parsedData["~referring_link"] ||
          parsedData["original_branch_link"]
        ) {
          useBranchStore.getState().setBranchParams(parsedData);

          // Extract attribution data for the event
          const link =
            (parsedData["original_branch_link"] as string | undefined) ??
            parsedData["~referring_link"];
          if (link) {
            const withoutQueryParams = link.split("?")[0];
            const branch_link = withoutQueryParams?.replace(
              "lsn-alternate.app.link",
              "lsn.app.link",
            );

            // Fire branch_link_opened event
            track("branch_link_opened", {
              branch_link,
              marketing_title:
                parsedData["$marketing_title"]?.toString() ?? null,
              tags: parsedData["~tags"] ?? [],
              link_title: parsedData["$link_title"]?.toString() ?? null,
              channel: parsedData["~channel"]?.toString() ?? null,
              feature: parsedData["~feature"]?.toString() ?? null,
              campaign: parsedData["~campaign"]?.toString() ?? null,
              link_type: parsedData["linkType"]?.toString() ?? null,
            });
          }
        }
        await handleLink(parsedData);
      }
    });
  }, []);
};

const useBranchProcessLinkActions = (
  linkActions: LinkAction[],
  setLinkActions: Dispatch<SetStateAction<LinkAction[]>>,
) => {
  const { firebaseUser: user, status } = useAuth();

  useEffect(() => {
    const processLinkActions = async () => {
      const remainingLinkActions: LinkAction[] = [];
      let hasActioned = false;

      for (const linkAction of linkActions) {
        const actioned = await linkAction(status, user);

        if (actioned) {
          hasActioned = true;
        } else {
          remainingLinkActions.push(linkAction);
        }
      }

      setLinkActions((currentLinkActions: LinkAction[]) => {
        return hasActioned ? remainingLinkActions : currentLinkActions;
      });
    };

    processLinkActions().catch((error) => {
      captureException(error, {
        channel: "error",
        area: "branch",
        operation: "process-link-actions-web",
      });
    });
  }, [linkActions, status, user]);
};

const useBranch = () => {
  const [linkActions, setLinkActions] = useState<LinkAction[]>([]);
  const updateUser = useUpdateUser();

  const handleLink = useCallback(async (data: BranchParams) => {
    const linkType = data.linkType as BranchLinkType | undefined;

    switch (linkType) {
      case undefined: {
        break;
      }
      case "direct-link": {
        setLinkActions((currentLinkActions: LinkAction[]) => [
          ...currentLinkActions,
          generateDirectLinkAction(data as unknown as DirectLink),
        ]);
        break;
      }
      case "custom-action-link": {
        setLinkActions((currentLinkActions: LinkAction[]) => [
          ...currentLinkActions,
          generateCustomActionLinkAction(
            data as unknown as CustomActionLink,
            updateUser,
          ),
        ]);
        break;
      }
      case "login-link": {
        setLinkActions((currentLinkActions: LinkAction[]) => [
          ...currentLinkActions,
          generateLoginLinkAction(data as unknown as LoginLink),
        ]);
        break;
      }
      default: {
        console.error("Invalid link type");
      }
    }
  }, []);

  useBranchProcessLinkActions(linkActions, setLinkActions);

  useBranchSubscribe(handleLink);

  useBranchIdentity();
};

export default useBranch;
